import { useState, useEffect, useRef } from 'react';
import { useDataProvider, useNotify, useRedirect } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField,
  Autocomplete, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import SaveIcon  from '@mui/icons-material/Save';
import AddIcon   from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import authFetch, { responseError } from '../utils/authFetch';
import { card, btn, inputSx, NAVY, typo } from '../styles/ds';

const CURRENCY_CHOICES = [{ id: 'ILS', label: '₪ שקל' }, { id: 'USD', label: '$ דולר' }];

const fmtCost = (v, currency) => currency === 'USD'
  ? `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  : `₪${Number(v || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;
const fmtHistDate = d => d ? new Date(d).toLocaleDateString('he-IL') : '—';

// ── Quick Add Supplier Dialog ─────────────────────────────────────────────────
const AddSupplierDialog = ({ open, onClose, onAdded }) => {
  const notify = useNotify();
  const [name,  setName]  = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { notify('נא להזין שם ספק', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch('/suppliers', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone || null, email: email || null }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בהוספת ספק'), { type: 'error' }); return; }
      const supplier = await res.json();
      notify(`הספק "${supplier.name}" נוסף`, { type: 'success' });
      onAdded({ id: supplier.id, label: supplier.name });
      setName(''); setPhone(''); setEmail('');
      onClose();
    } catch { notify('שגיאה בהוספת ספק', { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY }}>
        ספק חדש
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          <TextField label="שם הספק *" value={name} onChange={e => setName(e.target.value)}
            fullWidth sx={inputSx} autoFocus />
          <TextField label="טלפון" value={phone} onChange={e => setPhone(e.target.value)}
            fullWidth sx={inputSx} />
          <TextField label="אימייל" value={email} onChange={e => setEmail(e.target.value)}
            fullWidth sx={inputSx} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
          הוסף ספק
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main PurchaseCreate ────────────────────────────────────────────────────────
const PurchaseCreate = () => {
  const dp       = useDataProvider();
  const notify   = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();
  const location = useLocation();

  const preSupplier = location.state?.supplier_id
    ? { id: location.state.supplier_id, label: location.state.supplier_name || `ספק #${location.state.supplier_id}` }
    : null;
  const preVariant = location.state?.variant_id
    ? { id: location.state.variant_id, label: location.state.variant_name || `וריאנט #${location.state.variant_id}` }
    : null;

  const [variantId,    setVariantId]    = useState(preVariant);
  const [supplierId,   setSupplierId]   = useState(preSupplier);
  const [quantity,     setQuantity]     = useState(Number(location.state?.quantity) || 1);
  const [unitCost,     setUnitCost]     = useState('');
  const [currency,     setCurrency]     = useState('ILS');
  const [status,       setStatus]       = useState('received');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [notes,        setNotes]        = useState('');
  const [saving,       setSaving]       = useState(false);
  const [addSupplier,  setAddSupplier]  = useState(false);

  const [variants,        setVariants]        = useState([]);
  const [varLoading,      setVarLoading]      = useState(true);
  const [suppliers,       setSuppliers]       = useState([]);
  const [supLoading,      setSupLoading]      = useState(true);
  const [purchaseHistory, setPurchaseHistory] = useState(null); // past purchases of this variant from this supplier
  const [histLoading,     setHistLoading]     = useState(false);
  const autoFilledFor = useRef(null); // `${variantId}-${supplierId}` key

  const loadSuppliers = () => {
    authFetch('/suppliers?_start=0&_end=500&_sort=name&_order=ASC')
      .then(r => r.json())
      .then(d => { setSuppliers(Array.isArray(d) ? d : []); setSupLoading(false); })
      .catch(() => setSupLoading(false));
  };

  useEffect(() => {
    authFetch('/variants?_start=0&_end=9999&_sort=name&_order=ASC')
      .then(r => r.json())
      .then(d => { setVariants(Array.isArray(d) ? d : []); setVarLoading(false); })
      .catch(() => setVarLoading(false));
    loadSuppliers();
  }, []);

  // Purchase history for this variant+supplier — drives both the price
  // auto-fill and the small history table below the form.
  useEffect(() => {
    if (!variantId?.id || !supplierId?.id) { setPurchaseHistory(null); return; }
    setHistLoading(true);
    authFetch(`/variants/${variantId.id}/purchase-history?supplier_id=${supplierId.id}`)
      .then(r => r.json())
      .then(d => setPurchaseHistory(d.rows || []))
      .catch(() => setPurchaseHistory([]))
      .finally(() => setHistLoading(false));
  }, [variantId?.id, supplierId?.id]);

  // Auto-fill the cost from the most recent purchase of this variant from
  // this supplier, once per pair — a manual edit to the field cancels it.
  useEffect(() => {
    if (!variantId?.id || !supplierId?.id || !purchaseHistory) return;
    const key = `${variantId.id}-${supplierId.id}`;
    if (autoFilledFor.current === key) return;
    const last = purchaseHistory[0];
    if (last) {
      setUnitCost(String(last.unit_cost));
      setCurrency(last.currency);
      autoFilledFor.current = key;
    }
  }, [variantId?.id, supplierId?.id, purchaseHistory]);

  const variantOptions  = variants.map(v => ({ id: v.id, label: v.name || `וריאנט #${v.id}` }));
  const supplierOptions = suppliers.map(s => ({ id: s.id, label: s.name }));

  const handleSave = async () => {
    if (!variantId) { notify('נא לבחור מוצר', { type: 'warning' }); return; }
    if (!unitCost || Number(unitCost) <= 0) { notify('נא להזין עלות', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const { data } = await dp.create('purchases', {
        data: {
          variant_id:    variantId.id,
          supplier_id:   supplierId?.id || null,
          quantity:      Number(quantity) || 1,
          unit_cost:     Number(unitCost),
          currency,
          status,
          purchase_date: purchaseDate || null,
          notes:         notes || null,
        },
      });
      notify(status === 'ordered' ? 'הזמנת הרכש נשמרה — המלאי יתעדכן בקליטה' : 'הרכישה נשמרה בהצלחה', { type: 'success' });
      if (data.usd_cost_converted) {
        notify('עלות הרכישה הומרה אוטומטית ל-₪ לפי שער הדולר הנוכחי', { type: 'info' });
      } else if (data.usd_cost_rate_missing) {
        notify('לא הוגדר שער דולר — עלות הרכישה ב-₪ לא עודכנה', { type: 'warning' });
      }
      redirect('/purchases');
    } catch (e) {
      console.error(e);
      notify(e?.body?.error || 'שגיאה בשמירה', { type: 'error' });
    } finally { setSaving(false); }
  };

  return (
    <Box className="crm-page" p={3}>
      <Typography {...typo.pageTitle} mb={3}>רכישה חדשה</Typography>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>

            {/* Variant */}
            <Autocomplete
              options={variantOptions} loading={varLoading} value={variantId}
              onChange={(_, v) => setVariantId(v)}
              getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={p => <TextField {...p} label="מוצר *" sx={inputSx}
                InputProps={{ ...p.InputProps, endAdornment: <>{varLoading && <CircularProgress size={16}/>}{p.InputProps.endAdornment}</> }} />}
            />

            {/* Supplier + quick-add button */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                sx={{ flex: 1 }}
                options={supplierOptions} loading={supLoading} value={supplierId}
                onChange={(_, v) => setSupplierId(v)}
                getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={p => <TextField {...p} label="ספק" sx={inputSx} />}
              />
              <Button
                variant="contained" size="small"
                onClick={() => setAddSupplier(true)}
                sx={{ backgroundColor: NAVY, color: '#fff', borderRadius: 2, minWidth: 40, height: 40, mt: 0.2, px: 1.2,
                  '&:hover': { backgroundColor: '#162d5a' } }}
                title="הוסף ספק חדש"
              >
                <AddIcon fontSize="small" />
              </Button>
            </Stack>

            {/* Quantity */}
            <TextField label="כמות *" type="number" value={quantity}
              onChange={e => setQuantity(e.target.value)} inputProps={{ min: 1 }} sx={inputSx} />

            {/* Unit cost — auto-filled from the last purchase of this variant from this supplier */}
            <Box>
              <TextField label="עלות ליחידה *" type="number" value={unitCost} fullWidth
                onChange={e => { setUnitCost(e.target.value); autoFilledFor.current = null; }}
                inputProps={{ min: 0, step: '0.01' }} sx={inputSx} />
              {purchaseHistory?.[0] && (
                <Typography fontSize={11} color="text.secondary" mt={0.4}>
                  מחיר ברכישה האחרונה מספק זה: {fmtCost(purchaseHistory[0].unit_cost, purchaseHistory[0].currency)}
                </Typography>
              )}
            </Box>

            {/* Currency */}
            <FormControl fullWidth>
              <InputLabel>מטבע</InputLabel>
              <Select value={currency} onChange={e => setCurrency(e.target.value)} label="מטבע" sx={inputSx}>
                {CURRENCY_CHOICES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Status: received now vs purchase order awaiting goods */}
            <FormControl fullWidth>
              <InputLabel>סטטוס</InputLabel>
              <Select value={status} onChange={e => setStatus(e.target.value)} label="סטטוס" sx={inputSx}>
                <MenuItem value="received">התקבל — נכנס למלאי מיד</MenuItem>
                <MenuItem value="ordered">הוזמן — ימתין לקליטת סחורה</MenuItem>
              </Select>
            </FormControl>

            {/* Date */}
            <TextField label="תאריך רכישה" type="date" value={purchaseDate}
              onChange={e => setPurchaseDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx} />
          </Box>

          <TextField label="הערות" multiline rows={3} value={notes}
            onChange={e => setNotes(e.target.value)} fullWidth sx={{ mt: 2, ...inputSx }} />

          {/* Purchase history for this variant + supplier */}
          {supplierId?.id && variantId?.id && (
            <Box mt={2.5}>
              <Typography fontSize={12} fontWeight={700} color="text.secondary" mb={0.5}>
                רכישות קודמות של מוצר זה מספק זה
              </Typography>
              {histLoading ? (
                <CircularProgress size={16} />
              ) : !purchaseHistory?.length ? (
                <Typography fontSize={12} color="text.secondary">אין רכישות קודמות</Typography>
              ) : (
                <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small" sx={{ maxWidth: 480 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { backgroundColor: 'rgba(13,29,62,0.07)', fontSize: 11, py: 0.5, fontWeight: 700 } }}>
                      <TableCell>תאריך</TableCell>
                      <TableCell align="center">כמות</TableCell>
                      <TableCell align="center">עלות ליחידה</TableCell>
                      <TableCell align="center">סטטוס</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseHistory.map(p => (
                      <TableRow key={p.id} sx={{ '& td': { fontSize: 12, py: 0.5 } }}>
                        <TableCell>{fmtHistDate(p.purchase_date)}</TableCell>
                        <TableCell align="center">{p.quantity}</TableCell>
                        <TableCell align="center">{fmtCost(p.unit_cost, p.currency)}</TableCell>
                        <TableCell align="center">{p.status === 'ordered' ? 'הוזמן' : 'התקבל'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Stack className="crm-actions" direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate(-1)} sx={btn.cancel}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={btn.primaryLg}>
          שמור רכישה
        </Button>
      </Stack>

      {/* Quick Add Supplier Dialog */}
      <AddSupplierDialog
        open={addSupplier}
        onClose={() => setAddSupplier(false)}
        onAdded={newSupplier => {
          setSuppliers(prev => [...prev, { id: newSupplier.id, name: newSupplier.label }]);
          setSupplierId(newSupplier);
          loadSuppliers(); // refresh full list
        }}
      />
    </Box>
  );
};

export default PurchaseCreate;
